import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { MessageCircle, X, ChevronLeft, Send, Mail, RotateCcw, FileText } from 'lucide-react';
import { API_BASE } from '../config';

const SESSION_KEY = 'sf_helpbot_open';
const MAX_PROBLEM_LENGTH = 1000;

// Button-driven help widget — no AI/LLM, no third-party service. Mounted
// once in App.js alongside EditAccessToastHost/UpdateBannerHost so it shows
// on every logged-in page in the main app (not on /warehouse, which is a
// separate top-level branch in App.js, or /pod-upload, excluded below since
// that route shares the same mount point but is meant to stay a minimal
// mobile page).
export default function HelpChatbot({ user }) {
  const location = useLocation();
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch { return false; }
  });
  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [stack, setStack] = useState([{ name: 'menu' }]);
  const [problemText, setProblemText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  const screen = stack[stack.length - 1];

  // motion/react's AnimatePresence drives its transition via JS (WAAPI/RAF),
  // not CSS — a `transition: none` CSS rule has no effect on it, so
  // prefers-reduced-motion has to be read here and fed into the transition
  // prop directly.
  const [reducedMotion] = useState(() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  });

  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, open ? '1' : '0'); } catch { /* ignore */ }
  }, [open]);

  const loadContent = useCallback(() => {
    if (content || loadError) return;
    fetch(`${API_BASE}/chatbot?action=content`)
      .then(res => { if (!res.ok) throw new Error('fetch failed'); return res.json(); })
      .then(data => setContent(data))
      .catch(() => setLoadError(true));
  }, [content, loadError]);

  useEffect(() => { if (open) loadContent(); }, [open, loadContent]);

  // Esc closes and returns focus to the trigger button — standard dialog
  // keyboard behavior; this repo's other modals (TutorialWelcomeModal) don't
  // implement this yet, so there's no precedent to reuse here.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!user || location.pathname.startsWith('/pod-upload')) return null;

  function push(next) { setStack(prev => [...prev, next]); }
  function goBack() { setStack(prev => (prev.length > 1 ? prev.slice(0, -1) : prev)); }
  function goMenu() {
    setStack([{ name: 'menu' }]);
    setProblemText('');
    setSendError('');
  }
  function toggleOpen() {
    if (open) {
      setOpen(false);
      goMenu();
    } else {
      setOpen(true);
    }
  }

  const firstName = (user.display_name || user.username || '').trim().split(/\s+/)[0] || 'there';

  async function sendHelpRequest() {
    const trimmed = problemText.trim();
    if (!trimmed) return;
    setSending(true);
    setSendError('');
    try {
      const res = await fetch(`${API_BASE}/chatbot?action=help`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, problem: trimmed }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not send your problem. Please try again.');
      }
      push({ name: 'help-sent' });
    } catch (err) {
      setSendError(err.message);
    }
    setSending(false);
  }

  function mailtoLink() {
    const subject = encodeURIComponent('Portal help request');
    const body = encodeURIComponent(problemText.trim());
    const email = content?.helpEmail || 'automation@soulflower.in';
    return `mailto:${email}?subject=${subject}&body=${body}`;
  }

  return (
    <div style={S.root} className="sf-helpbot-root">
      <style>{EMBEDDED_CSS}</style>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Help"
            className="sf-helpbot-panel"
            style={S.panel}
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: reducedMotion ? 0 : 0.18 }}
          >
            <div style={S.header}>
              <span style={S.headerTitle}>Help</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {stack.length > 1 && (
                  <button type="button" onClick={goMenu} style={S.headerIconBtn} title="Restart" aria-label="Restart">
                    <RotateCcw size={15} />
                  </button>
                )}
                <button type="button" onClick={() => { setOpen(false); goMenu(); }} style={S.headerIconBtn} title="Close" aria-label="Close">
                  <X size={17} />
                </button>
              </div>
            </div>

            <div style={S.body}>
              {loadError ? (
                <BotScreen>
                  <p style={S.text}>Sorry, something went wrong loading help content. Please try again shortly.</p>
                </BotScreen>
              ) : !content ? (
                <BotScreen><p style={S.text}>Loading…</p></BotScreen>
              ) : screen.name === 'menu' ? (
                <MenuScreen firstName={firstName} content={content} push={push} />
              ) : screen.name === 'sops' ? (
                <SopDeptScreen content={content} push={push} goMenu={goMenu} />
              ) : screen.name === 'sop-list' ? (
                <SopListScreen department={screen.department} content={content} push={push} goBack={goBack} goMenu={goMenu} />
              ) : screen.name === 'sop-detail' ? (
                <SopDetailScreen sop={screen.sop} user={user} goBack={goBack} goMenu={goMenu} />
              ) : screen.name === 'features' ? (
                <FeatureListScreen content={content} push={push} goMenu={goMenu} />
              ) : screen.name === 'feature-detail' ? (
                <FeatureDetailScreen feature={screen.feature} goBack={goBack} goMenu={goMenu} />
              ) : screen.name === 'help-prompt' ? (
                <HelpPromptScreen content={content} push={push} goMenu={goMenu} />
              ) : screen.name === 'help-form' ? (
                <HelpFormScreen
                  content={content} problemText={problemText} setProblemText={setProblemText}
                  sending={sending} sendError={sendError} onSend={sendHelpRequest} goMenu={goMenu}
                />
              ) : screen.name === 'help-sent' ? (
                <HelpSentScreen content={content} mailtoLink={mailtoLink} goMenu={goMenu} />
              ) : screen.name === 'soulbot-chat' ? (
                <SoulBotChatScreen user={user} goMenu={goMenu} />
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        style={S.bubble}
        aria-label={open ? 'Close help' : 'Open help'}
        aria-expanded={open}
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Screens                                                                  */
/* ---------------------------------------------------------------------- */

function BotScreen({ children }) {
  return <div style={S.screen}>{children}</div>;
}

function BackToMenu({ goMenu, goBack }) {
  return (
    <div style={S.footerRow}>
      {goBack && <button type="button" onClick={goBack} style={S.ghostBtn}><ChevronLeft size={14} /> Back</button>}
      <button type="button" onClick={goMenu} style={S.ghostBtn}>Back to menu</button>
    </div>
  );
}

function MenuScreen({ firstName, content, push }) {
  const greeting = (content.messages?.greeting || 'Hi {name}, what can I help you with?').replace('{name}', firstName);
  return (
    <BotScreen>
      <p style={S.text}>{greeting}</p>
      <div style={S.optionCol}>
        <button type="button" style={S.optionBtn} onClick={() => push({ name: 'soulbot-chat' })}>🤖 Ask SoulBot</button>
        <button type="button" style={S.optionBtn} onClick={() => push({ name: 'sops' })}>SOPs</button>
        <button type="button" style={S.optionBtn} onClick={() => push({ name: 'features' })}>Portal features</button>
        <button type="button" style={S.optionBtn} onClick={() => push({ name: 'help-prompt' })}>I need help</button>
      </div>
    </BotScreen>
  );
}

function SopDeptScreen({ content, push, goMenu }) {
  const departments = [...new Set((content.sops || []).map(s => s.department))];
  return (
    <BotScreen>
      <p style={S.text}>Which department's SOPs are you looking for?</p>
      <div style={S.optionCol}>
        {departments.length === 0 && <p style={S.mutedText}>No SOPs available yet.</p>}
        {departments.map(dept => (
          <button key={dept} type="button" style={S.optionBtn} onClick={() => push({ name: 'sop-list', department: dept })}>{dept}</button>
        ))}
      </div>
      <BackToMenu goMenu={goMenu} />
    </BotScreen>
  );
}

function SopListScreen({ department, content, push, goBack, goMenu }) {
  const sops = (content.sops || []).filter(s => s.department === department);
  return (
    <BotScreen>
      <p style={S.text}>{department} SOPs</p>
      <div style={S.optionCol}>
        {sops.map(sop => (
          <button key={sop.id} type="button" style={S.optionBtn} onClick={() => push({ name: 'sop-detail', sop })}>{sop.title}</button>
        ))}
      </div>
      <BackToMenu goMenu={goMenu} goBack={goBack} />
    </BotScreen>
  );
}

function SopDetailScreen({ sop, user, goBack, goMenu }) {
  const openUrl = `${API_BASE}/chatbot?action=sop&id=${encodeURIComponent(sop.id)}&user_id=${encodeURIComponent(user.id)}`;
  return (
    <BotScreen>
      <p style={S.cardTitle}>{sop.title}</p>
      <p style={S.text}>{sop.summary}</p>
      <p style={S.mutedText}>Last updated: {sop.updated}</p>
      <a href={openUrl} target="_blank" rel="noreferrer" style={S.primaryBtnLink}>
        <FileText size={14} /> Open SOP
      </a>
      <BackToMenu goMenu={goMenu} goBack={goBack} />
    </BotScreen>
  );
}

function FeatureListScreen({ content, push, goMenu }) {
  return (
    <BotScreen>
      <p style={S.text}>Which module do you want to know about?</p>
      <div style={S.optionCol}>
        {(content.features || []).map(f => (
          <button key={f.id} type="button" style={S.optionBtn} onClick={() => push({ name: 'feature-detail', feature: f })}>{f.name}</button>
        ))}
      </div>
      <BackToMenu goMenu={goMenu} />
    </BotScreen>
  );
}

function FeatureDetailScreen({ feature, goBack, goMenu }) {
  return (
    <BotScreen>
      <p style={S.cardTitle}>{feature.name}</p>
      <p style={S.text}>{feature.description}</p>
      <p style={S.mutedText}>Used by: {feature.usedBy}</p>
      <a href={feature.link} style={S.primaryBtnLink}>Open {feature.name}</a>
      <BackToMenu goMenu={goMenu} goBack={goBack} />
    </BotScreen>
  );
}

function HelpPromptScreen({ content, push, goMenu }) {
  return (
    <BotScreen>
      <p style={S.text}>{content.messages?.helpPrompt || 'Do you need any help?'}</p>
      <div style={S.optionCol}>
        <button type="button" style={S.optionBtn} onClick={() => push({ name: 'help-form' })}>Yes</button>
        <button type="button" style={S.optionBtn} onClick={goMenu}>No</button>
      </div>
    </BotScreen>
  );
}

function HelpFormScreen({ content, problemText, setProblemText, sending, sendError, onSend, goMenu }) {
  return (
    <BotScreen>
      <p style={S.text}>{content.messages?.helpAsk || 'Please describe your problem'}</p>
      <textarea
        value={problemText}
        onChange={(e) => setProblemText(e.target.value.slice(0, MAX_PROBLEM_LENGTH))}
        maxLength={MAX_PROBLEM_LENGTH}
        rows={4}
        placeholder="Describe your problem…"
        style={S.textarea}
      />
      <p style={S.charCount}>{problemText.length}/{MAX_PROBLEM_LENGTH}</p>
      {sendError && <p style={S.errorText}>{sendError}</p>}
      <button type="button" style={S.primaryBtn} onClick={onSend} disabled={sending || !problemText.trim()}>
        <Send size={14} /> {sending ? 'Sending…' : 'Send'}
      </button>
      <BackToMenu goMenu={goMenu} />
    </BotScreen>
  );
}

function HelpSentScreen({ content, mailtoLink, goMenu }) {
  return (
    <BotScreen>
      <p style={S.text}>{content.messages?.helpReply || 'Thanks for sharing. Please email your problem to automation@soulflower.in and the team will help you.'}</p>
      <a href={mailtoLink()} style={S.primaryBtnLink}>
        <Mail size={14} /> Email us
      </a>
      <BackToMenu goMenu={goMenu} />
    </BotScreen>
  );
}

// SoulBot — live data Q&A (sales/DMS/inventory), the same bot brain that
// already runs on Telegram/WhatsApp, as a 3rd channel. Unlike the rest of
// this file's screens (static content, browsed via buttons), this is a
// free-text chat transcript: the real durable memory lives server-side
// (chat_sessions / bot_inbound_chat / bot_outbound_chat, same as the other
// 2 channels) — this component's `messages` state is just a display of the
// current session, not the source of truth, so losing it on refresh is
// expected/fine (same as opening a fresh Telegram chat loses nothing real).
function getSoulBotAuthHeaders(user) {
  return {
    'x-admin-user': user.username,
    'x-admin-pass': localStorage.getItem('sf_pass') || '',
  };
}

function SoulBotChatScreen({ user, goMenu }) {
  const [messages, setMessages] = useState([
    { role: 'bot', text: `Hey ${(user.display_name || user.username || '').trim().split(/\s+/)[0] || 'there'}! 👋 Ask me about Sales, DMS distributor stock, or our Inventory.` },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, sending]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setMessages(prev => [...prev, { role: 'user', text }]);
    setInput('');
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/soulbot-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSoulBotAuthHeaders(user) },
        body: JSON.stringify({ message: text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
      setMessages(prev => [...prev, { role: 'bot', text: data.reply || '(no reply)' }]);
    } catch (err) {
      setError(err.message);
    }
    setSending(false);
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  return (
    <div style={S.soulbotScreen}>
      <div ref={bodyRef} style={S.soulbotTranscript}>
        {messages.map((m, i) => (
          <div key={i} style={m.role === 'user' ? S.soulbotBubbleUser : S.soulbotBubbleBot}>{m.text}</div>
        ))}
        {sending && <div style={S.soulbotBubbleBot}><em>Typing…</em></div>}
        {error && <p style={S.errorText}>{error}</p>}
      </div>
      <div style={S.soulbotInputRow}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask SoulBot…"
          rows={1}
          style={S.soulbotTextarea}
          disabled={sending}
        />
        <button type="button" onClick={send} disabled={sending || !input.trim()} style={S.soulbotSendBtn} aria-label="Send">
          <Send size={16} />
        </button>
      </div>
      <BackToMenu goMenu={goMenu} />
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Styles — self-scoped tokens (no shared global token file exists in this */
/* app; mirrors the grn- and sbn- precedent with the same brand colors).   */
/* ---------------------------------------------------------------------- */

const EMBEDDED_CSS = `
:root {
  --helpbot-purple: #6E58A6;
  --helpbot-purple-tint: #EEE9F8;
  --helpbot-ink: #231C30;
  --helpbot-muted: #8C82A0;
  --helpbot-border: #ECE7F2;
  --helpbot-error: #B3261E;
}
.sf-helpbot-panel { font-family: 'Poppins', 'Segoe UI', sans-serif; }
.sf-helpbot-panel button:focus-visible,
.sf-helpbot-panel a:focus-visible { outline: 2px solid var(--helpbot-purple); outline-offset: 2px; }
@media (max-width: 480px) {
  .sf-helpbot-panel {
    position: fixed !important; inset: auto 0 0 0 !important;
    width: 100% !important; max-width: none !important;
    height: min(80vh, 560px) !important; border-radius: 16px 16px 0 0 !important;
  }
}
`;

const S = {
  root: { position: 'fixed', right: 20, bottom: 20, zIndex: 1300 },
  bubble: {
    width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'var(--helpbot-purple, #6E58A6)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(110,88,166,0.4)', marginLeft: 'auto',
  },
  panel: {
    position: 'absolute', right: 0, bottom: 72, width: 340, maxWidth: 'calc(100vw - 40px)',
    maxHeight: 520, display: 'flex', flexDirection: 'column',
    background: '#fff', borderRadius: 16, overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(35,28,48,0.25)', border: '1px solid var(--helpbot-border, #ECE7F2)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', background: 'var(--helpbot-purple, #6E58A6)', color: '#fff', flex: 'none',
  },
  headerTitle: { fontWeight: 600, fontSize: 14.5 },
  headerIconBtn: {
    width: 28, height: 28, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  },
  body: { flex: 1, overflowY: 'auto', padding: 16 },
  screen: { display: 'flex', flexDirection: 'column', gap: 10 },
  text: { fontSize: 13.5, color: 'var(--helpbot-ink, #231C30)', lineHeight: 1.5, margin: 0 },
  mutedText: { fontSize: 11.5, color: 'var(--helpbot-muted, #8C82A0)', margin: 0 },
  errorText: { fontSize: 12, color: 'var(--helpbot-error, #B3261E)', margin: 0 },
  cardTitle: { fontSize: 14.5, fontWeight: 600, color: 'var(--helpbot-ink, #231C30)', margin: 0 },
  optionCol: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 2 },
  optionBtn: {
    textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--helpbot-border, #ECE7F2)',
    background: 'var(--helpbot-purple-tint, #EEE9F8)', color: 'var(--helpbot-purple, #6E58A6)',
    fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10,
    border: 'none', background: 'var(--helpbot-purple, #6E58A6)', color: '#fff', fontWeight: 600,
    fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', width: 'fit-content',
  },
  primaryBtnLink: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10,
    textDecoration: 'none', background: 'var(--helpbot-purple, #6E58A6)', color: '#fff', fontWeight: 600,
    fontSize: 13, fontFamily: 'inherit', width: 'fit-content',
  },
  ghostBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 12px', borderRadius: 8,
    border: '1px solid var(--helpbot-border, #ECE7F2)', background: '#fff', color: 'var(--helpbot-muted, #8C82A0)',
    fontWeight: 500, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
  },
  footerRow: { display: 'flex', gap: 8, marginTop: 8 },
  textarea: {
    width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--helpbot-border, #ECE7F2)',
    fontFamily: 'inherit', fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
  },
  charCount: { fontSize: 10.5, color: 'var(--helpbot-muted, #8C82A0)', textAlign: 'right', margin: 0 },
  soulbotScreen: { display: 'flex', flexDirection: 'column', gap: 8, height: '100%' },
  soulbotTranscript: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 200, maxHeight: 320, padding: '2px 2px' },
  soulbotBubbleUser: {
    alignSelf: 'flex-end', maxWidth: '85%', padding: '8px 12px', borderRadius: '12px 12px 2px 12px',
    background: 'var(--helpbot-purple, #6E58A6)', color: '#fff', fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap',
  },
  soulbotBubbleBot: {
    alignSelf: 'flex-start', maxWidth: '85%', padding: '8px 12px', borderRadius: '12px 12px 12px 2px',
    background: 'var(--helpbot-purple-tint, #EEE9F8)', color: 'var(--helpbot-ink, #231C30)', fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap',
  },
  soulbotInputRow: { display: 'flex', gap: 6, alignItems: 'flex-end' },
  soulbotTextarea: {
    flex: 1, padding: '8px 10px', borderRadius: 10, border: '1px solid var(--helpbot-border, #ECE7F2)',
    fontFamily: 'inherit', fontSize: 13, resize: 'none', boxSizing: 'border-box', maxHeight: 80,
  },
  soulbotSendBtn: {
    width: 34, height: 34, flex: 'none', borderRadius: 10, border: 'none', cursor: 'pointer',
    background: 'var(--helpbot-purple, #6E58A6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
